/**
 * A message the administrator sends themselves.
 *
 * COPY, NOT SEND. Nothing here reaches an email server and nothing says a
 * message was sent, because neither is true. Building delivery would mean
 * deciding what "from StandingDocs" means on a message a person wrote, and
 * that is a decision to take deliberately rather than by accident.
 *
 * The body is editable before it is copied: a prepared message that cannot be
 * adjusted is one somebody rewrites in their mail client anyway.
 */
import { useState } from 'react';
import { DESCRIPTION_MAX } from '../../../types/fieldLimits';
import page from '../../../shared/Page.module.css';
import own from './Onboarding.module.css';
import { mailtoFor, prepareMessage, ROLE_LABEL } from './messageFor';
import type { Onboarding, Requirement } from './types';
import { primaryFor } from './people';
import { GroupCard } from '../../../shared/GroupCard';

export function ContactMessage({
  data, role, onClose,
}: {
  data: Onboarding;
  role: string;
  onClose: () => void;
}) {
  const recipient = primaryFor(data.people, role) ?? null;
  const wanted: Requirement[] = data.attention.filter(
    (r) => r.blocking && r.owner === 'customer' && r.contactRole === role,
  );

  const prepared = prepareMessage({
    orgName: data.orgName,
    requirements: wanted,
    recipient: recipient ? { name: recipient.name, email: recipient.email } : null,
    intakeLinkSent: data.intakeLinkSent,
  });

  const [body, setBody] = useState(prepared.body);
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (what: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
    } catch {
      // A clipboard that refuses is not an error worth a dialog: the text is
      // on screen and selectable, which is the fallback either way.
      setCopied(null);
    }
  };

  const roleLabel = ROLE_LABEL[role] ?? role;

  return (
    <GroupCard id="message-title" title={`Message for the ${roleLabel.toLowerCase()} contact`}
      aside={<button type="button" className={page.btn} onClick={onClose}>Close</button>}>

      {!recipient && (
        <div className={`${page.notice} ${page.noticeWarn}`} role="alert">
          Nobody holds the {roleLabel.toLowerCase()} role yet, so there is no address to send
          this to. Name one on the Contacts stage first.
        </div>
      )}

      <div className={page.facts}>
        <div>
          <dt className={page.factTerm}>To</dt>
          <dd className={page.factValue}>
            {recipient ? `${recipient.name} · ${recipient.email}` : <span className={page.absent}>Nobody named</span>}
          </dd>
        </div>
        <div>
          <dt className={page.factTerm}>Subject</dt>
          <dd className={page.factValue}>{prepared.subject}</dd>
        </div>
      </div>

      <label htmlFor="message-body" className={page.itemMeta} style={{ display: 'block', marginTop: 'var(--sp-4)' }}>
        Edit it before you send it if you want to.
      </label>
      <textarea
        id="message-body"
        className={own.message}
        /*
         * Bounded like every other field, though nothing here is saved: the
         * mandate is that no text input is unbounded, and a message nobody
         * caps is one somebody pastes a policy library into by accident.
         * DESCRIPTION_MAX leaves room to add context without pasting a library in.
         */
        maxLength={DESCRIPTION_MAX}
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />

      <div className={page.actions} style={{ marginTop: 'var(--sp-3)' }}>
        <button type="button" className={`${page.btn} ${page.btnPrimary}`}
          onClick={() => copy('message', body)}>Copy message</button>
        <button type="button" className={page.btn}
          onClick={() => copy('subject', prepared.subject)}>Copy subject</button>
        <button type="button" className={page.btn} disabled={!recipient}
          onClick={() => recipient && copy('address', recipient.email)}>Copy email address</button>
        {recipient && (
          <a className={page.btn} href={mailtoFor({ ...prepared, body })}>Open in your email client</a>
        )}
      </div>

      {copied && (
        <p className={page.itemMeta} role="status">
          {copied === 'message' ? 'Message' : copied === 'subject' ? 'Subject' : 'Address'} copied.
          Nothing has been sent — send it yourself.
        </p>
      )}
      <p className={page.itemMeta}>
        StandingDocs does not send this for you. It is written for you to send from your own
        address, so a reply reaches you.
      </p>
    </GroupCard>
  );
}
